/**
 * Generic point marker layer factory.
 * Renders the majority of event-based layers as ScatterplotLayer dots,
 * colored by severity and sized proportionally.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { NormalizedEvent, Severity } from '@/sources/base-source';
import type { PickingInfo } from '@deck.gl/core';
import { SEVERITY_COLORS, POINT_LAYER_COLORS } from '@/map/layer-colors';

/** Layer IDs that use the generic point renderer (circles only). */
export const POINT_LAYER_IDS = new Set([
  'refugee-camps', 'humanitarian-ops',
  'sanctions-markers',
  'protests-cluster',
  'nasa-events',
]);

const SEVERITY_COLOR: Record<Severity, [number, number, number]> = {
  critical: SEVERITY_COLORS.critical,
  high: SEVERITY_COLORS.high,
  medium: SEVERITY_COLORS.medium,
  low: SEVERITY_COLORS.low,
  info: SEVERITY_COLORS.info,
};

/** Per-layer flat color overrides matching WorldMonitor. */
const LAYER_COLOR_OVERRIDE: Record<string, [number, number, number]> = POINT_LAYER_COLORS;

const SEVERITY_RADIUS: Record<Severity, number> = {
  critical: 8,
  high: 6,
  medium: 5,
  low: 3,
  info: 2,
};

/**
 * Zoom-dependent alpha matching Worldmonitor's formula (DeckGLMap.ts:2941):
 * alphaScale = min(1, (zoom - 2.5) / 2.5), floored at 0.3.
 * Reaches full alpha at zoom 5 — the (zoom - 1) / 8 variant that shipped
 * first reached it at zoom 9 and washed out every layer at working zooms.
 * Shared across point, icon, and clustering layers.
 */
export function getZoomAlpha(zoom: number): number {
  const alphaScale = Math.max(0.3, Math.min(1, (zoom - 2.5) / 2.5));
  const alpha = Math.round(160 * alphaScale);
  return alpha / 255;
}

export type LayerEventHandler = (info: PickingInfo<NormalizedEvent>) => void;

/** Callbacks for point interactions — wired in Step 3b. */
export interface PointLayerCallbacks {
  onHover?: LayerEventHandler;
  onClick?: LayerEventHandler;
}

/** Create a ScatterplotLayer for generic point event data. */
export function createPointLayer(
  layerId: string,
  events: NormalizedEvent[],
  zoom: number,
  callbacks?: PointLayerCallbacks,
): ScatterplotLayer<NormalizedEvent> {
  const opacity = getZoomAlpha(zoom);

  return new ScatterplotLayer<NormalizedEvent>({
    id: `point-${layerId}`,
    data: events,
    pickable: true,
    opacity,
    filled: true,
    radiusUnits: 'pixels' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: LAYER_COLOR_OVERRIDE[layerId]
      ? LAYER_COLOR_OVERRIDE[layerId]
      : (d) => SEVERITY_COLOR[d.severity ?? 'info'],
    getRadius: (d) => SEVERITY_RADIUS[d.severity ?? 'info'],
    onHover: callbacks?.onHover,
    onClick: callbacks?.onClick,
    updateTriggers: {
      getFillColor: [events.length],
      getRadius: [events.length],
    },
  });
}

/**
 * Pulse animation layer for critical/high severity events.
 * Semi-transparent ScatterplotLayer behind markers with oscillating radius
 * using Date.now() % 3000 (same pattern as hotspot-layer.ts).
 */
export function createPulseLayer(
  events: NormalizedEvent[],
  zoom: number,
): ScatterplotLayer<NormalizedEvent> | null {
  const pulseEvents = events.filter(
    (e) => e.location && (e.severity === 'critical' || e.severity === 'high'),
  );
  if (pulseEvents.length === 0) return null;

  const now = Date.now();
  const pulse = 1.0 + 0.8 * (0.5 + 0.5 * Math.sin(now / 400));
  const pulseScale = pulse;
  const pulseAlpha = Math.round(120 * (1 - 0.4 * (0.5 + 0.5 * Math.sin(now / 400))));

  return new ScatterplotLayer<NormalizedEvent>({
    id: 'pulse-critical',
    data: pulseEvents,
    pickable: false,
    filled: true,
    radiusUnits: 'pixels' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => {
      const base = SEVERITY_COLOR[d.severity ?? 'critical'];
      return [base[0], base[1], base[2], pulseAlpha];
    },
    getRadius: (d) => SEVERITY_RADIUS[d.severity ?? 'critical'] * pulseScale,
    opacity: getZoomAlpha(zoom),
    updateTriggers: {
      getRadius: [now],
      getFillColor: [now],
    },
  });
}

export { SEVERITY_COLOR, SEVERITY_RADIUS };

/** Pulse animation parameters computed from a timestamp. */
export interface PulseParams {
  scale: number;
  alpha: number;
}

/** Compute pulse animation scale/alpha from current time. Same sine math as createPulseLayer. */
export function computePulseParams(nowMs: number): PulseParams {
  const sinVal = 0.5 + 0.5 * Math.sin(nowMs / 400);
  return {
    scale: 1.0 + 0.8 * sinVal,
    alpha: Math.round(120 * (1 - 0.4 * sinVal)),
  };
}
